<template>
  <div class="list-container">
    <div v-show="!dialogFormVisible">
      <!-- 顶部视图切换 Tabs -->
      <div class="main-tab-header">
        <el-tabs v-model="mainActiveTab" type="card" @tab-change="handleMainTabChange">
          <el-tab-pane label="人员提成汇总" name="summary" />
          <el-tab-pane label="收款业务明细" name="detail" />
        </el-tabs>
      </div>

      <!-- Tab 1: 人员提成汇总视图 -->
      <div v-show="mainActiveTab === 'summary'" class="summary-container">
        <!-- 汇总筛选栏 -->
        <div class="filter-container">
          <div class="filter-box">
            <span class="label">开始日期</span>
            <el-date-picker
              v-model="listQuery.startTime"
              type="date"
              placeholder=" "
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              @change="handleFilter"
              :size="t('table.btnSize')"
            />
          </div>
          <div class="filter-box">
            <span class="label">结束日期</span>
            <el-date-picker
              v-model="listQuery.endTime"
              type="date"
              placeholder=" "
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              @change="handleFilter"
              :size="t('table.btnSize')"
            />
          </div>
          <div class="filter-box">
            <span class="label">人员信息</span>
            <el-select
              :filterable="true"
              v-model="listQuery.personnelId"
              class="filter-item"
              placeholder="全部人员(销售/工程师)"
              clearable
              :size="t('table.btnSize')"
              @focus="handlePersonnelFocus"
              @change="handleFilter"
            >
              <el-option
                v-for="item in personnelOption"
                :key="item.id"
                :label="item.mark"
                :value="item.id"
              />
            </el-select>
          </div>
          <div class="filter-box">
            <span class="label">所属公司</span>
            <el-select
              :filterable="true"
              v-model="listQuery.titleId"
              class="filter-item"
              placeholder=""
              clearable
              :size="t('table.btnSize')"
              @focus="handleFilterTitleFocus"
              @change="handleFilter"
            >
              <el-option
                v-for="item in getTitleCompanyOptions()"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
          <div class="filter-box">
            <el-button
              v-waves
              class="filter-item"
              type="primary"
              :size="t('table.btnSize')"
              @click="handleFilter"
            >
              {{ t('table.search') }}
            </el-button>
            <el-button
              v-waves
              class="filter-item"
              :size="t('table.btnSize')"
              @click="handleReset"
            >
              {{ t('table.reset') }}
            </el-button>
            <el-button
              v-waves
              class="filter-item"
              type="success"
              :size="t('table.btnSize')"
              @click="handleExportSummary"
            >
              导出汇总
            </el-button>
            <span class="svg-icon cursor mr-10 ml-10" @click="getSummaryList">
              <i class="icon-tubiao-27 iconfont"/>
            </span>
          </div>
        </div>

        <!-- KPI 核心指标统计卡片条 -->
        <div class="kpi-card-row mb-15">
          <div class="kpi-card">
            <div class="kpi-title">涉及人员数</div>
            <div class="kpi-value text-primary">{{ kpiStats.totalPersons }} <span class="kpi-unit">人</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">关联业务单数</div>
            <div class="kpi-value">{{ kpiStats.totalCount }} <span class="kpi-unit">单</span></div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">累计已收款总额</div>
            <div class="kpi-value">¥{{ formatMoney(kpiStats.totalReceivedFee) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">累计已开票总额</div>
            <div class="kpi-value">¥{{ formatMoney(kpiStats.totalInvoicePrice) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">累计税后总成本</div>
            <div class="kpi-value">¥{{ formatMoney(kpiStats.totalCost) }}</div>
          </div>
          <div class="kpi-card highlight-green">
            <div class="kpi-title">累计已收款提成</div>
            <div class="kpi-value text-green">¥{{ formatMoney(kpiStats.totalReceivedCommission) }}</div>
          </div>
          <div class="kpi-card highlight-blue">
            <div class="kpi-title">累计已开票提成</div>
            <div class="kpi-value text-blue">¥{{ formatMoney(kpiStats.totalInvoiceCommission) }}</div>
          </div>
        </div>

        <!-- 汇总表格 -->
        <div class="summary-table-wrapper">
          <el-table
            v-loading="summaryLoading"
            :data="summaryList"
            border
            stripe
            style="width: 100%"
            show-summary
            :summary-method="getSummaryRow"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <el-table-column prop="userName" label="人员姓名" min-width="110" fixed="left">
              <template #default="{ row }">
                <span style="font-weight: 600;">{{ row.userName }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="deptName" label="所属部门" min-width="120" />
            <el-table-column prop="role" label="岗位角色" min-width="120" align="center">
              <template #default="{ row }">
                <el-tag
                  :type="row.role.includes('销售') && row.role.includes('工程师') ? 'danger' : row.role.includes('销售') ? 'primary' : 'success'"
                  size="small"
                >
                  {{ row.role }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="businessCount" label="业务单数" min-width="90" align="center" sortable />
            <el-table-column prop="totalReceivedFee" label="已收款总额" min-width="130" align="right" sortable>
              <template #default="{ row }">
                ¥{{ formatMoney(row.totalReceivedFee) }}
              </template>
            </el-table-column>
            <el-table-column prop="totalInvoicePrice" label="已开票总额" min-width="130" align="right" sortable>
              <template #default="{ row }">
                ¥{{ formatMoney(row.totalInvoicePrice) }}
              </template>
            </el-table-column>
            <el-table-column prop="totalPartsCostWithoutTax" label="税后出库成本" min-width="120" align="right">
              <template #default="{ row }">
                ¥{{ formatMoney(row.totalPartsCostWithoutTax) }}
              </template>
            </el-table-column>
            <el-table-column prop="totalOutsourceCostWithoutTax" label="税后外包成本" min-width="120" align="right">
              <template #default="{ row }">
                ¥{{ formatMoney(row.totalOutsourceCostWithoutTax) }}
              </template>
            </el-table-column>
            <el-table-column prop="totalMarketCost" label="市场成本" min-width="110" align="right">
              <template #default="{ row }">
                ¥{{ formatMoney(row.totalMarketCost) }}
              </template>
            </el-table-column>
            <el-table-column prop="totalCostWithoutTax" label="税后总成本" min-width="120" align="right">
              <template #default="{ row }">
                ¥{{ formatMoney(row.totalCostWithoutTax) }}
              </template>
            </el-table-column>
            <el-table-column prop="receivedCommission" label="已收款提成" min-width="130" align="right" sortable>
              <template #default="{ row }">
                <span style="color: #67c23a; font-weight: bold;">¥{{ formatMoney(row.receivedCommission) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="invoiceCommission" label="已开票提成" min-width="130" align="right" sortable>
              <template #default="{ row }">
                <span style="color: #409eff; font-weight: bold;">¥{{ formatMoney(row.invoiceCommission) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="110" align="center" fixed="right">
              <template #default="{ row }">
                <el-button
                  type="primary"
                  link
                  size="small"
                  @click="handleViewPersonnelDetail(row)"
                >
                  查看明细
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Tab 2: 收款业务明细大表视图 -->
      <div v-show="mainActiveTab === 'detail'" class="detail-container">
        <div class="filter-container">
          <template v-if="!isFilterCollapse">
            <div class="filter-box">
              <span class="label">客户名称</span>
              <el-input
                v-model="listQuery.customerCompanyName"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">业务编号</span>
              <el-input
                v-model="listQuery.no"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">业务品牌</span>
              <el-input
                v-model="listQuery.brand"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">业务名称</span>
              <el-input
                v-model="listQuery.name"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">型号</span>
              <el-input
                v-model="listQuery.model"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">人员信息</span>
              <el-select
                :filterable="true"
                v-model="listQuery.personnelId"
                class="filter-item"
                placeholder="全部人员(销售/工程师)"
                clearable
                :size="t('table.btnSize')"
                @focus="handlePersonnelFocus"
                @change="handleFilter"
              >
                <el-option
                  v-for="item in personnelOption"
                  :key="item.id"
                  :label="item.mark"
                  :value="item.id"
                />
              </el-select>
            </div>
            <div class="filter-box">
              <span class="label">订单号码</span>
              <el-input
                v-model="listQuery.customerOrderNo"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">发票号码</span>
              <el-input
                v-model="listQuery.invoiceNo"
                style="width: 200px"
                :size="t('table.btnSize')"
                class="filter-item"
                clearable
                @keyup.enter="handleFilter"
              />
            </div>
            <div class="filter-box">
              <span class="label">开票状态</span>
              <el-select :filterable="true" v-model="listQuery.invoiceStatus" class="filter-item" placeholder="" @change="handleFilter" clearable :size="t('table.btnSize')">
                <el-option
                  v-for="item in statusOption"
                  :key="item.id"
                  :label="item.mark"
                  :value="item.id"
                />
              </el-select>
            </div>
            <div class="filter-box">
              <span class="label">税率</span>
              <el-select :filterable="true" v-model="listQuery.rate" class="filter-item" placeholder="" @change="handleFilter" clearable :size="t('table.btnSize')">
                <el-option
                  v-for="item in rateOption"
                  :key="item.id"
                  :label="item.mark"
                  :value="item.id"
                />
              </el-select>
            </div>
            <div class="filter-box">
              <span class="label">开始日期</span>
              <el-date-picker
                v-model="listQuery.startTime"
                type="date"
                placeholder=" "
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                @change="handleFilter"
                :size="t('table.btnSize')"
              >
              </el-date-picker>
            </div>
            <div class="filter-box">
              <span class="label">结束日期</span>
              <el-date-picker
                v-model="listQuery.endTime"
                type="date"
                placeholder=" "
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                @change="handleFilter"
                :size="t('table.btnSize')"
              >
              </el-date-picker>
            </div>
            <div class="filter-box" >
              <span class="label">所属公司</span>
              <el-select :filterable="true" v-model="listQuery.titleId" class="filter-item" placeholder="" clearable :size="t('table.btnSize')" @focus="handleFilterTitleFocus()" @change="handleFilter">
                <el-option
                  v-for="item in getTitleCompanyOptions()"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </div>
          </template>
          <div class="filter-box">
            <el-button
              v-waves
              class="filter-item "
              type="primary"
              :size="t('table.btnSize')"
              @click="handleFilter"
            >
              {{ t('table.search') }}
            </el-button>
            <el-button
              v-permission="{ pkey: `${permissionName}_ADD` }"
              class="filter-item"
              type="primary"
              :size="t('table.btnSize')"
              @click="handleCommand('add')"
            >
              {{ t('table.add') }}
            </el-button>
            <el-button
              v-permission="{ pkey: `${permissionName}_BATCH_RECEIVE` }"
              class="filter-item"
              type="primary"
              :size="t('table.btnSize')"
              @click="handleCommand('batch-fee')"
            >
              批量收款
            </el-button>
            <el-button
              v-waves
              class="filter-item"
              :size="t('table.btnSize')"
              @click="handleReset"
            >
              {{ t('table.reset') }}
            </el-button>
            <el-button
              v-waves
              class="filter-item"
              :size="t('table.btnSize')"
              @click="isFilterCollapse = !isFilterCollapse"
            >
              {{ isFilterCollapse ? '展开' : '收起' }}
            </el-button>
            <span class="svg-icon cursor mr-10 ml-10" @click="handleCommand('refresh')">
              <i class="icon-tubiao-27 iconfont"/>
            </span>
            <span class="svg-icon cursor" @click="handleCommand('column')">
              <i class="icon-tubiao-28 iconfont"/>
            </span>
          </div>
        </div>
        <grid-layout
          v-model:layout="layouts"
          :col-num="12"
          :row-height="rowHeight"
          :is-draggable="false"
          :is-resizable="true"
          :is-mirrored="false"
          :vertical-compact="true"
          :margin="[10, 10]"
          :use-css-transforms="false"
        >
          <grid-item
            v-for="(item, index) in layouts"
            :x="item.x"
            :y="item.y"
            :w="item.w"
            :h="item.h"
            :i="item.i"
            :key="item.i"
            :minW="item.minW"
            :maxW="item.maxW"
            :minH="item.minH"
            :static="item.static"
            @resize="handleResizedEvent"
            @resized="handleResizedEvent">
            <template v-if="index === 0">
              <TableList
                :ref="rf => { if (rf) tableDataRef[index] = rf }"
                v-loading="listLoading"
                :dataList="list"
                :columnAllData="columnAllData"
                :tableName="TableName"
                :permissionName="permissionName"
                :showSelection="true"
                :isShowPagination="false"
                :handleDefaultWidth="80"
                :highlightCurrentRow="true"
                :layoutHeight="true"
                :newHpx="newHpx"
                :showSummary="true"
                style="height:100% ;overflow:hidden;"
                @row-click="handleRowClick"
                @command-change="handleCommand"
              >
                <template v-slot:operation-action="scope">
                  <el-link
                    v-permission="{ pkey: `${permissionName}_FEE` }"
                    href="javascript:void(0);"
                    class="mr-10"
                    type="primary"
                    @click="handleCommand('fee', scope.row)"
                  >
                    收款
                  </el-link>
                </template>
              </TableList>
              <el-pagination
                class="mt-20"
                v-show="total > 0"
                :total="total"
                v-model:currentPage="listQuery.page"
                :page-size="listQuery.size"
                :page-sizes="pageSizes"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                layout="total, sizes, prev, pager, next"
              />
            </template>
            <template v-if="index === 1">
              <div v-show="!isSubMenuCollapse">
                <div class="clearfix tab-title btn-container mt-10">
                  <ul class="tab-title_left fl">
                    <li
                        v-for="item in tabList"
                        :key="item.id"
                        :class="{ active: activeTab === item.id }"
                        @click="handleTabClick(item)">{{ item.name }}
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </grid-item>
        </grid-layout>
      </div>

    </div>
    <FormComponent
      v-if="dialogFormVisible"
      :id="activeId"
      :initFormData="initFormData"
      :fromFieldData="fromFieldData"
      :dialogFormVisible="dialogFormVisible"
      :api="'business'"
      :commandState="commandState"
      @update:visibel="() => dialogFormVisible = false"
      @submit="handleSubmit()"
    />
  </div>
</template>

<script lang='ts'>
import { defineComponent, reactive, toRefs, onMounted, ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { cloneDeep } from 'lodash'
import { getDetailById, deleteById, getPageList, postUrl } from '@/apis/Dictionary'
import { getPageList as getUrlList } from '@/apis/Form'
import { getUserDetailByUserId, dropdownPersonnelUsers } from '@/apis/user'
import { getPersonnelCommissionSummary } from '@/apis/stat'
import { PersonnelCommissionSummary } from '@/types/domain'
import { exportJson2Excel } from '@/utils/excel'
import FormComponent from '@/components/Form/index.vue'
import TableList from '@/components/common/TableList.vue'

import { TableName, permissionName } from '@/utils/enum'
import { FromFieldData } from './FieldFrom'
import { TableField } from './FieldTable'
import { getCurrentMonthRange, pageVo, pageSizes, numberTimes, totalPriceWithoutRate } from '@/utils/index'
import { useI18n } from 'vue-i18n'
import mixin from '@/mixins'
import $number from 'number-precision'
import $currency from 'currency.js'

export default defineComponent({
  name: 'Financialfee',
  components: {
    FormComponent,
    TableList
  },
  setup() {
    const { t } = useI18n()
    const ContactList = ref(null)
    const EngineerInfoList = ref(null)
    const TraceInfo = ref(null)
    const tableDataRef = ref([])

    const dataMap = reactive({
      TableName: TableName.TABLE_RECEIVEBUSINESSDETAIL1,
      permissionName: permissionName.RECEIVEBUSINESSDETAI1,
      initFormData: cloneDeep(FromFieldData),
      fromFieldData: cloneDeep(FromFieldData),
      columnAllData: TableField,

      // 主选项卡: 'summary' 人员提成汇总 | 'detail' 收款业务明细
      mainActiveTab: 'summary',
      summaryLoading: false,
      summaryList: [] as PersonnelCommissionSummary[],
      personnelOption: [] as any[],

      activeId: 0,
      activeRow: {} as any,
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        name: undefined,
        brand: undefined,
        model: undefined,
        serial: undefined,
        type: undefined,
        urgentId: undefined,
        customerCompanyName: undefined,
        customerCompanyContactName: undefined,
        customerCompanyContactPhone: undefined,
        salesManId: undefined,
        engineerId: undefined,
        personnelId: undefined,
        outsourceCompanyName: undefined,
        outsourceCompanyContactName: undefined,
        outsourceCompanyContactPhone: undefined,
        startTime: '',
        endTime: '',
        receivedStatus: '0,2',
        titleId: '',
        rate: null,
        ...pageVo
      },
      pageSizes,
      dialogFormVisible: false,

      tabList: [
        {
          name: '收费明细',
          id: 5
        }
      ],
      activeTab: 5,

      salesManId: '',
      newHpx: 0,
      urgentOption: [],
      businessTypeOption: [],
      engineerOption: [],
      selectionArray: [],
      titleCompanyOptions: [],
      commandState: 'edit',
      selectcCustomerCompanyId: 0,
      chargeVisible: false,
      achieveType: '',
      accountOption: [],
      selectRow: [],
      statusOption: [],
      rateOption: [],

      formatMoney(val: any): string {
        if (val == null || isNaN(val)) {
          return '0.00'
        }
        return $currency(val).toString()
      },

      handleMainTabChange(tabName: any) {
        dataMap.mainActiveTab = tabName
        if (tabName === 'summary') {
          dataMap.getSummaryList()
        } else {
          dataMap.getList()
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
          }, 50)
        }
      },

      handleViewPersonnelDetail(row: PersonnelCommissionSummary) {
        dataMap.listQuery.personnelId = row.userId
        dataMap.mainActiveTab = 'detail'
        dataMap.listQuery.page = 1
        dataMap.getList()
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 50)
      },

      async getSummaryList() {
        const {
          startTime,
          endTime,
          personnelId,
          titleId
        } = dataMap.listQuery
        const tempsStart = startTime ? new Date(startTime).getTime() : 0
        const tempdEnd = endTime ? new Date(endTime).getTime() : 0
        if (!tempsStart) {
          return ElMessage.error({
            message: '请选择开始时间',
            duration: 1000
          })
        } else if (!tempdEnd) {
          return ElMessage.error({
            message: '请选择结束时间',
            duration: 1000
          })
        } else if (tempsStart > tempdEnd) {
          return ElMessage.error({
            message: '开始时间必须小于结束时间',
            duration: 1000
          })
        }
        dataMap.summaryLoading = true
        try {
          const res = await getPersonnelCommissionSummary({
            startTime,
            endTime,
            personnelId: personnelId || undefined,
            titleId: titleId ? Number(titleId) : undefined
          })
          dataMap.summaryList = res || []
        } catch (e) {
          dataMap.summaryList = []
        } finally {
          dataMap.summaryLoading = false
        }
      },

      getSummaryRow(param: { columns: any[]; data: any[] }) {
        const { columns, data } = param
        const sums: string[] = []
        columns.forEach((column, index) => {
          if (index === 0) {
            sums[index] = '合计'
            return
          }
          if (column.property === 'businessCount') {
            const values = data.map(item => Number(item[column.property]) || 0)
            const sum = values.reduce((prev, curr) => prev + curr, 0)
            sums[index] = `${sum} 单`
            return
          }
          if ([
            'totalReceivedFee',
            'totalInvoicePrice',
            'totalPartsCostWithoutTax',
            'totalOutsourceCostWithoutTax',
            'totalMarketCost',
            'totalCostWithoutTax',
            'receivedCommission',
            'invoiceCommission'
          ].includes(column.property)) {
            const values = data.map(item => Number(item[column.property]) || 0)
            const sum = values.reduce((prev, curr) => $number.plus(prev, curr), 0)
            sums[index] = `¥${$currency(sum).toString()}`
            return
          }
          sums[index] = ''
        })
        return sums
      },

      handleExportSummary() {
        if (!dataMap.summaryList.length) {
          return ElMessage.warning('暂无数据可导出')
        }
        const headers = [
          '人员姓名',
          '所属部门',
          '岗位角色',
          '业务单数',
          '已收款总额',
          '已开票总额',
          '税后出库成本',
          '税后外包成本',
          '市场成本',
          '税后总成本',
          '已收款提成',
          '已开票提成'
        ]
        const data = dataMap.summaryList.map((item: any) => [
          item.userName,
          item.deptName,
          item.role,
          item.businessCount,
          item.totalReceivedFee,
          item.totalInvoicePrice,
          item.totalPartsCostWithoutTax,
          item.totalOutsourceCostWithoutTax,
          item.totalMarketCost,
          item.totalCostWithoutTax,
          item.receivedCommission,
          item.invoiceCommission
        ])
        exportJson2Excel(headers, data, `人员提成汇总_${dataMap.listQuery.startTime}_至_${dataMap.listQuery.endTime}`)
      },

      async handleCommand(command: string, val?: any) {
        dataMap.commandState = command
        if (command !== 'column') {
          dataMap.activeRow = val
          dataMap.activeId = val ? val.id : 0
        }
        switch (command) {
          case 'add':
            dataMap.handleCreate()
            break
          case 'copy':
          case 'edit':
            dataMap.handleUpdate(val)
            break
          case 'delete':
            dataMap.handleDelete(val)
            break
          case 'refresh':
            dataMap.handleFilter()
            break
          case 'column':
            (tableDataRef.value[0] as any).setColumnVisible = true
            break
          case 'current-change':
            dataMap.handleCurrentChange(val)
            break
          case 'size-change':
            dataMap.handleSizeChange(val)
            break
          case 'selection':
            dataMap.selectionArray = val
            break
          case 'fee': {
            const { id, changeStatus } = val
            const onFeeClickFun = () => {
              const businessIds = [id]
              postUrl('business/actions/verify/receive', { ids: businessIds }).then(() => {
                dataMap.chargeVisible = true
                dataMap.selectRow = [val]
              })
            }
            if (changeStatus === '申请取消') {
              ElMessageBox.confirm('此业务正在申请取消，是否继续收款？', {
                title: '提示',
                confirmButtonText: '继续',
                cancelButtonText: '取消'
              }).then(() => onFeeClickFun())
              break
            }
            onFeeClickFun()
          }
            break
          case 'batch-fee':
            {
              if (!dataMap.selectionArray.length) {
                return ElMessage.error({
                  message: '请选择业务',
                  duration: 1000
                })
              }

              dataMap.selectcCustomerCompanyId = dataMap.selectionArray[0].customerCompanyId
              for (let i = 0; i < dataMap.selectionArray.length; i++) {
                const item = dataMap.selectionArray[i]
                if (dataMap.selectcCustomerCompanyId !== item.customerCompanyId) {
                  return ElMessage.error({
                    message: '请选择同一个客户公司名称业务',
                    duration: 1000
                  })
                }
              }
              const businessIds = dataMap.selectionArray.map((kk: any) => kk.id)
              postUrl('business/actions/verify/receive', { ids: businessIds }).then(() => {
                dataMap.chargeVisible = true
                dataMap.selectRow = dataMap.selectionArray
              })
            }

            break
        }
      },
      multi(num1: number | string, num2: number | string): number {
        return this.multiWithCurrency(num1, num2, false) as number
      },
      multiWithCurrency(num1: number | string, num2: number | string, isCurrency: boolean): $currency | number {
        if (isCurrency) {
          return $currency($number.times(num1, num2))
        }
        return $number.times(num1, num2)
      },
      handleRowClick(row: any) {
        dataMap.activeId = row.id
      },
      calcHeight() {
        return 'calc(100% - 50px)'
      },
      handleTabCommand(command: string) {
        if (this.activeTab === 1) {
          if (ContactList.value) {
            ;(ContactList.value as any).handleCommand(command)
          }
        } else if (this.activeTab === 2) {
          if (EngineerInfoList.value) {
            ;(EngineerInfoList.value as any).handleCommand(command)
          }
        } else if (this.activeTab === 3) {
          if (TraceInfo.value) {
            ;(TraceInfo.value as any).handleCommand(command)
          }
        }
      },
      getList() {
        const {
          startTime,
          endTime
        } = dataMap.listQuery
        const tempsStart = startTime ? new Date(startTime).getTime() : 0
        const tempdEnd = endTime ? new Date(endTime).getTime() : 0
        if (!tempsStart) {
          return ElMessage.error({
            message: '请选择开始时间',
            duration: 1000
          })
        } else if (!tempdEnd) {
          return ElMessage.error({
            message: '请选择结束时间',
            duration: 1000
          })
        } else if (tempsStart > tempdEnd) {
          return ElMessage.error({
            message: '开始时间必须小于结束时间',
            duration: 1000
          })
        }
        dataMap.listLoading = true
        getPageList('business', {
          ...dataMap.listQuery,
          receivedStatus: '2,3',
          pageType: 'RECEIVED'
        }).then((res: any) => {
          dataMap.list = res.data?.map((item: any) => {
            const { orderFee = 0, amount = 0, orderTaxRate = 0 } = item
            return {
              ...item,
              orderFee4: dataMap.numberTimes(orderFee, amount, true),
              orderFee3: dataMap.totalPriceWithoutRate(orderFee, amount, true, orderTaxRate)
            }
          }) || []
          dataMap.total = res.total
          dataMap.listLoading = false
          dataMap.activeId = dataMap.list.length ? dataMap.list[0].id : 0
        }).catch(() => {
          dataMap.listLoading = false
        })
      },
      numberTimes: (price: any, amount: any, isCurrency: boolean) => numberTimes(price, amount, isCurrency),
      totalPriceWithoutRate: (price: any, amount: any, isCurrency: boolean, rate: any) => totalPriceWithoutRate(price, amount, isCurrency, rate),

      handleReset() {
        dataMap.listQuery = {
          name: undefined,
          brand: undefined,
          model: undefined,
          serial: undefined,
          type: undefined,
          urgentId: undefined,
          customerCompanyName: undefined,
          customerCompanyContactName: undefined,
          customerCompanyContactPhone: undefined,
          salesManId: undefined,
          engineerId: undefined,
          personnelId: undefined,
          outsourceCompanyName: undefined,
          outsourceCompanyContactName: undefined,
          outsourceCompanyContactPhone: undefined,
          ...getCurrentMonthRange(),
          receivedStatus: '0,2',
          titleId: '',
          rate: null,
          ...pageVo
        }
        dataMap.handleFilter()
      },
      handleFilter() {
        if (dataMap.mainActiveTab === 'summary') {
          dataMap.getSummaryList()
        } else {
          dataMap.listQuery.page = 1
          dataMap.getList()
        }
      },

      handleCreate() {
        dataMap.activeId = 0
        dataMap.fromFieldData = cloneDeep(dataMap.initFormData)
        dataMap.dialogFormVisible = true
      },
      handleUpdate(row: any) {
        dataMap.activeId = row.id
        dataMap.fromFieldData = cloneDeep(dataMap.initFormData)
        getDetailById('business', row.id).then((res: any) => {
          dataMap.fromFieldData.forEach((k: any) => {
            k.formList.forEach(async(field: any) => {
              field.value = res[field.fieldKey] || field.value
              switch (field.fieldKey) {
                case 'customerContactIds': {
                  const result = await getUrlList(field.config.url, { companyId: res.customerCompanyId })
                  field.option = result
                }
                  break
                case 'customerCompanyId': {
                  const result = await getUrlList('customerCompany/dropdown', {
                    ids: res.customerCompanyId
                  })
                  field.option = result.map((item: any) => {
                    return {
                      ...item
                    }
                  })
                }
                  break
                case 'salesManId': {
                  const result = await getUserDetailByUserId(res.salesManId)
                  field.option = [{
                    id: result.id,
                    name: result.name
                  }]
                }
                  break
              }
            })
          })
          dataMap.dialogFormVisible = true
        })
      },
      handleDelete(row: any) {
        ElMessageBox.confirm(
          '确定要删除选中数据吗？',
          '删除',
          {
            confirmButtonText: t('table.confirm'),
            cancelButtonText: t('table.cancel'),
            type: 'warning',
            cancelButtonClass: 'el-button--danger'
          }
        )
          .then(() => {
            deleteById('business', row.id).then(() => {
              dataMap.getList()
              ElMessage({
                message: '删除成功',
                type: 'success',
                duration: 1000
              })
            })
          })
      },
      handleSubmit() {
        dataMap.getList()
      },

      handleCurrentChange(page?: any) {
        dataMap.listQuery.page = page
        dataMap.getList()
      },
      handleSizeChange(size: any) {
        dataMap.listQuery.size = size
        dataMap.handleCurrentChange(1)
      },
      getDropdownList() {
        getUrlList('dropdown/BusinessInvoicingStatus').then((res) => {
          dataMap.statusOption = res || []
        })
        getUrlList('feeRate/dropdowns').then((res) => {
          dataMap.rateOption = res
            ? res.map((item: any) => {
              return {
                id: item,
                mark: `${item}%`
              }
            })
            : []
        })
      },
      handleEngineerFocus() {
        getUrlList('user/dropdown/engineers?all=true').then((res: any) => {
          dataMap.engineerOption = res || []
        })
      },
      handleFilterTitleFocus() {
        getUrlList('ticketTitle/dropdown').then((res: any) => {
          dataMap.titleCompanyOptions = res || []
        })
      },
      getTitleCompanyOptions() {
        return dataMap.titleCompanyOptions ?? []
      },
      handlePersonnelFocus() {
        dropdownPersonnelUsers({ all: true }).then((res) => {
          dataMap.personnelOption = res || []
        })
      },
      handleTabClick(item: any) {
        dataMap.activeTab = item.id
      },
      handleResizedEvent(i: any, newH: any, newW: any, newHPx: any) {
        dataMap.newHpx = newHPx
      }
    })

    const kpiStats = computed(() => {
      const list = dataMap.summaryList || []
      const totalPersons = list.length
      let totalCount = 0
      let totalReceivedFee = 0
      let totalInvoicePrice = 0
      let totalCost = 0
      let totalReceivedCommission = 0
      let totalInvoiceCommission = 0
      for (const item of list) {
        totalCount += item.businessCount || 0
        totalReceivedFee = $number.plus(totalReceivedFee, item.totalReceivedFee || 0)
        totalInvoicePrice = $number.plus(totalInvoicePrice, item.totalInvoicePrice || 0)
        totalCost = $number.plus(totalCost, item.totalCostWithoutTax || 0)
        totalReceivedCommission = $number.plus(totalReceivedCommission, item.receivedCommission || 0)
        totalInvoiceCommission = $number.plus(totalInvoiceCommission, item.invoiceCommission || 0)
      }
      return {
        totalPersons,
        totalCount,
        totalReceivedFee,
        totalInvoicePrice,
        totalCost,
        totalReceivedCommission,
        totalInvoiceCommission
      }
    })

    const { mixinData } = mixin(dataMap)
    onMounted(() => {
      mixinData.initHeight()

      Object.assign(dataMap.listQuery, getCurrentMonthRange())
      dataMap.listQuery.receivedStatus = '0,2'
      dataMap.handlePersonnelFocus()
      dataMap.getSummaryList()
      // Tab 2 懒加载：切换到明细 Tab 时再请求，避免首屏同时请求两个大表
      dataMap.getDropdownList()
    })
    return {
      t,
      ...toRefs(dataMap),
      ...toRefs(mixinData),
      tableDataRef,
      kpiStats
    }
  }
})
</script>

<style lang="scss">
.grid-layout-container {
  .vue-grid-item {
    background: #666;
  }
}
</style>

<style lang="scss" scoped>
.main-tab-header {
  margin-bottom: 12px;
  :deep(.el-tabs__header) {
    margin-bottom: 0;
  }
}

.kpi-card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 15px;

  .kpi-card {
    flex: 1;
    min-width: 135px;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #e4e7ed;
    padding: 10px 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

    .kpi-title {
      font-size: 12px;
      color: #909399;
      margin-bottom: 5px;
    }

    .kpi-value {
      font-size: 17px;
      font-weight: bold;
      color: #303133;

      .kpi-unit {
        font-size: 12px;
        font-weight: normal;
        color: #909399;
      }
    }

    &.highlight-green {
      background: #f0f9eb;
      border-color: #e1f3d8;
      .kpi-value {
        color: #67c23a;
      }
    }

    &.highlight-blue {
      background: #ecf5ff;
      border-color: #d9ecff;
      .kpi-value {
        color: #409eff;
      }
    }
  }
}

.summary-table-wrapper {
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
}

.statusStyle {
  .status {
    padding: 0 4px;
    border-radius: 6px;
    margin-right: 7px;
    font-size: 10px;
    color: #fff;
  }
}

.redStatusStyle {
  .icon-tubiao-40 {
    color: #E74228;
  }

  .status {
    background-color: #E74228;
  }
}

.yellowStatusStyle {
  .icon-tubiao-40 {
    color: #F3D03C;
  }

  .status {
    background-color: #F3D03C;
  }
}

.warming-fp {
  border-radius: 4px;
  border: 1px solid $red;
  color: $red;
  padding: 8px;
  margin-bottom: 20px;
}
</style>
